<script setup>
import { useHeaderStore } from '@/stores/header';
import SessionsHeader from '~/components/blocks/two-six/sessions/header.vue';

const headerStore = useHeaderStore();
headerStore.altLogo++;
onUnmounted(() => {
    headerStore.altLogo--;
    headerStore.sessionYear = null;
});

const route = useRoute()
const agenda = route.params.id
const lowered = agenda.toLowerCase()

if (agenda !== lowered) {
    if (import.meta.server) {
        throw navigateTo(`/sessions/${lowered}`, { redirectCode: 301 });
    } else {
        await navigateTo(`/sessions/${lowered}`, { replace: true });
    }
}

// Resolve the session across known event years. Try the current year
// (2026, Sessionboard-backed) first, then fall back to the previous year
// (2025, Airtable-backed) so legacy Bizzabo-ID links keep working.
const findMatch = (sessions) => {
    if (!Array.isArray(sessions)) return null;
    return sessions.find((s) => {
        const sid = s?.id?.toLowerCase?.();
        const uid = s?.unique_id?.toLowerCase?.();
        return sid === lowered || uid === lowered;
    }) || null;
};

// useRequestFetch, not $fetch: during SSR a bare $fetch calls the API with no
// request headers, so /api/v1/sessions sees no session and applies the
// Partner-Day gate to EVERYONE — an approved partner would get a 404 on their
// own session's page. Forwarding the cookie makes the server render this page
// as the actual viewer. Safe to cache-key on: /sessions/** bypasses the worker
// cache for logged-in requests (nuxt.config routeRules), so a partner's
// Partner-Day page can never be stored and replayed to the public (UNB-600).
const requestFetch = useRequestFetch();

const { data, pending } = await useAsyncData(`session-page-${lowered}`, async () => {
    try {
        const res2026 = await requestFetch('/api/v1/sessions', {
            params: { id: lowered, year: 2026, unscheduled: true },
        });
        const match2026 = findMatch(res2026?.sessions);
        if (match2026) return { sessions: [match2026] };
    } catch {}
    try {
        const res2025 = await requestFetch('/api/v1/sessions', {
            params: { id: lowered, year: 2025 },
        });
        const match2025 = findMatch(res2025?.sessions);
        if (match2025) return { sessions: [match2025] };
    } catch {}
    return { sessions: [] };
});

const firstSession = data.value?.sessions?.[0];
if (!firstSession) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Session Not Found',
        fatal: true,
    });
}

// Tell the nav which event year this session belongs to so it can highlight
// the correct item (current-year → Agenda, archive → that year's archive
// link), since current-year and archive sessions share the /sessions/* URL.
watch(() => data.value?.sessions?.[0]?.year ?? null, (y) => {
    headerStore.sessionYear = y;
}, { immediate: true });

// If the URL param is the internal UUID, redirect to the friendly ID.
const canonicalId = firstSession.unique_id?.toLowerCase?.();
if (canonicalId && canonicalId !== lowered) {
    if (import.meta.server) {
        throw navigateTo(`/sessions/${canonicalId}`, { redirectCode: 301 });
    } else {
        await navigateTo(`/sessions/${canonicalId}`, { replace: true });
    }
}

useSeoMeta({
    title: data.value.sessions[0].title,
    ogTitle: data.value.sessions[0].title,
    description: data.value.sessions[0].description,
    ogDescription: data.value.sessions[0].description,
    ogImage: '/social.jpg',
    twitterCard: 'summary_large_image',
});

const page = computed(() => {
    if (!data.value?.sessions?.length) return false
    return { id: data.value.sessions[0].id, data: data.value.sessions[0], model: 'session' }
})

</script>

<template>
    <ProjectPage :page="page" data-model="agenda" v-if="!pending && page">
        <component :is="SessionsHeader" :session="data.sessions[0]" />
    </ProjectPage>
</template>